import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import getCurrencySymbol from "../../utils/getCurrencySymbol";
import useTimeSelection from "../hooks/selection/useTimeSelection";
import ExtendedIcon from "../shared/ExtendedIcon";
import GeneralPerformanceGraph from "./GeneralPerformanceGraph";
import { sliceToPeriod } from "./sliceToPeriod";

interface PerformanceHistoryEntry {
  timestamp: number;
  profit?: number | null;
  absoluteProfit?: number | null;
  drawdown?: number | null;
}

type PerformanceData = Record<string, PerformanceHistoryEntry[]>;

interface GeneralPerformanceGraphCardProps {
  isLoading?: boolean;
  data?: PerformanceData;
  mainCurrency?: string;
}

const GeneralPerformanceGraphCard = ({
  isLoading,
  data: rawData = {},
  mainCurrency,
}: GeneralPerformanceGraphCardProps) => {
  const performanceHistoryPerCurrency = rawData;
  const { t } = useTranslation("stats", { keyPrefix: "performanceGraphCard" });

  const { options: timeOptions, selectedOption: timeSelectedOption } = useTimeSelection("MONTH");

  const currencyCodes = Object.keys(performanceHistoryPerCurrency);
  const initialCurrency = mainCurrency && currencyCodes.includes(mainCurrency) ? mainCurrency : currencyCodes[0];
  const [selectedCurrency, setSelectedCurrency] = useState(initialCurrency);

  // Update selected currency if it's no longer available
  const activeCurrency =
    selectedCurrency && currencyCodes.includes(selectedCurrency) ? selectedCurrency : currencyCodes[0];

  // Calculate profit for each currency
  const currencyProfits = useMemo(() => {
    return currencyCodes.map((code) => {
      const history = performanceHistoryPerCurrency[code] || [];
      const { first: firstData, last: lastData } = sliceToPeriod(history, timeSelectedOption?.timestamp);
      const profit = (lastData?.profit || 0) - (firstData?.profit || 0);
      return {
        code,
        profit,
        symbol: getCurrencySymbol(code) || code,
        isPositive: profit >= 0,
      };
    });
  }, [currencyCodes, performanceHistoryPerCurrency, timeSelectedOption]);

  // Memoised so the slice below does not recompute on every render.
  const performanceHistory = useMemo(
    () => (activeCurrency ? performanceHistoryPerCurrency[activeCurrency] || [] : []),
    [activeCurrency, performanceHistoryPerCurrency]
  );

  const {
    points: periodPerformanceHistory,
    first: firstData,
    last: lastData,
  } = useMemo(
    () => sliceToPeriod(performanceHistory, timeSelectedOption?.timestamp),
    [performanceHistory, timeSelectedOption?.timestamp]
  );

  const showDrawdown = !!performanceHistory.find((d) => d?.drawdown !== undefined);
  const periodDrawdown = (lastData?.drawdown || 0) - (firstData?.drawdown || 0);

  return (
    <div className="card-modern card-featured mb-4">
      {/* Header */}
      <div className="card-header-modern">
        <div>
          <div className="card-title">
            <i className="fas fa-chart-area mr-2" style={{ opacity: 0.5 }} />
            {t("title")}
          </div>
        </div>

        <div className="card-actions">
          {/* Time Period Selector */}
          <div className="btn-group-modern">
            {timeOptions.map(({ isSelected, select, label, ...rest }, index) => (
              <button key={index} className={`btn-group-item ${isSelected ? "active" : ""}`} onClick={select}>
                {rest.isExtended && <ExtendedIcon className="mr-1" />}
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Metrics Bar - Currency Profit Pills */}
      <div
        style={{
          padding: "12px 24px",
          background: "linear-gradient(90deg, rgba(241, 245, 249, 0.5) 0%, transparent 100%)",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          borderBottom: "1px solid #e2e8f0",
          flexWrap: "wrap",
        }}
      >
        {/* Currency Profit Pills */}
        {currencyProfits.map(({ code, profit, symbol, isPositive }) => {
          const isActive = activeCurrency === code;
          const hasMultiple = currencyProfits.length > 1;
          return (
            <button
              key={code}
              onClick={() => hasMultiple && setSelectedCurrency(code)}
              className={`metric-badge ${isPositive ? "metric-positive" : "metric-negative"}`}
              style={{
                border: "none",
                cursor: hasMultiple ? "pointer" : "default",
                opacity: hasMultiple && !isActive ? 0.5 : 1,
                transform: isActive ? "scale(1.05)" : "scale(1)",
                transition: "all 0.2s ease",
                outline: isActive ? "2px solid rgba(255,255,255,0.8)" : "none",
                outlineOffset: "2px",
              }}
            >
              {isPositive ? "+" : ""}
              {profit.toFixed(2)} {symbol}
              <span style={{ opacity: 0.7, marginLeft: "4px", fontSize: "0.6875rem" }}>
                {isPositive ? t("profit") : t("losses")}
              </span>
            </button>
          );
        })}

        {/* Drawdown Badge */}
        {showDrawdown && (
          <div className="metric-badge metric-neutral">
            {periodDrawdown.toFixed(2)}%
            <span style={{ opacity: 0.7, marginLeft: "4px", fontSize: "0.6875rem" }}>{t("drawdown")}</span>
          </div>
        )}
      </div>

      {/* Chart */}
      <div className="card-body-modern" style={{ padding: "16px" }}>
        <div className="chart-container">
          <GeneralPerformanceGraph
            data={periodPerformanceHistory}
            currency={activeCurrency}
            isLoading={isLoading}
            style={{ height: "calc(100vh - 450px)", minHeight: 350 }}
          />
        </div>
      </div>
    </div>
  );
};

export default GeneralPerformanceGraphCard;
